import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";
import Sidebar from "@/layout/Sidebar";
import Header from "@/layout/Header";
import { supabase } from "@/lib/supabaseClient";
import { useImpersonation } from "@/context/ImpersonationContext";
import ImpersonationBanner from "@/components/impersonation/ImpersonationBanner";
import { TutorialProvider } from "@/tutorials/TutorialProvider";
import SupportChatBubble from "@/components/support/SupportChatBubble";
import { BranchProvider } from "@/context/BranchContext";

export default function AppShell() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [billingAccess, setBillingAccess] = useState(null);
  const [billingPortal, setBillingPortal] = useState(null);
  const [dismissedBillingBannerId, setDismissedBillingBannerId] = useState(null);
  const readOnlyCopy = readOnlyAccessCopy(billingAccess?.reason);
  const touchStartRef = useRef(null);
  const impersonation = useImpersonation();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      supabase.rpc("get_tenant_access_state"),
      supabase.rpc("tenant_get_billing_portal"),
    ]).then(([accessResult, portalResult]) => {
      if (cancelled) return;
      if (accessResult.status === "fulfilled") {
        setBillingAccess(accessResult.value?.data || null);
      } else {
        setBillingAccess(null);
      }
      if (portalResult.status === "fulfilled") {
        setBillingPortal(portalResult.value?.data || null);
      } else {
        setBillingPortal(null);
      }
    });
    return () => { cancelled = true; };
  }, []);

  function handleTouchStart(event) {
    if (window.innerWidth >= 1024) return;
    const touch = event.touches?.[0];
    if (!touch) return;
    if (!sidebarOpen && touch.clientX > 32) return;
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      openAtStart: sidebarOpen,
    };
  }

  function handleTouchEnd(event) {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start || window.innerWidth >= 1024) return;
    const touch = event.changedTouches?.[0];
    if (!touch) return;
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dx) < 56 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
    if (!start.openAtStart && start.x <= 32 && dx > 0) {
      setSidebarOpen(true);
    }
    if (start.openAtStart && dx < 0) {
      setSidebarOpen(false);
    }
  }

  const billingHardLocked = billingAccess?.mode === "paywall"
    && !["/settings/billing", "/settings/referrals"].includes(location.pathname);
  const billingBanner = buildBillingUrgencyBanner(billingPortal);
  const showBillingBanner = billingBanner && dismissedBillingBannerId !== billingBanner.dismissKey;
  const hardLockMessage = billingAccess?.reason === "suspended"
    ? "Your account has been suspended. Contact support or settle your outstanding balance."
    : "Your 14-day trial has ended. Select a plan to continue.";

  return (
    <div
      className="min-h-screen bg-[#F8FAFC] pt-5"
      data-testid="app-shell"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {impersonation.active ? (
        <ImpersonationBanner
          session={impersonation.session}
          ending={impersonation.ending}
          onEnd={impersonation.endSession}
        />
      ) : null}
      <BranchProvider>
        <Sidebar
          open={sidebarOpen}
          collapsed={sidebarCollapsed}
          onClose={() => setSidebarOpen(false)}
          onToggleCollapse={() => setSidebarCollapsed((value) => !value)}
        />
        {sidebarOpen ? (
          <div
            aria-hidden="true"
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 z-30 bg-slate-900/50 backdrop-blur-sm lg:hidden"
          />
        ) : null}
        {!sidebarOpen ? (
          <div
            aria-hidden="true"
            className="fixed left-0 top-24 z-20 h-24 w-2 rounded-r-full bg-slate-300/70 lg:hidden"
          />
        ) : null}
        <div className={[
          "min-h-screen flex flex-col transition-[margin] duration-200 ease-in-out",
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60",
        ].join(" ")}>
          <Header onMenuClick={() => setSidebarOpen(true)} />
          <main className="flex-1 px-4 sm:px-6 lg:px-10 py-6 sm:py-8">
            <TutorialProvider>
              {showBillingBanner ? (
                <GlobalBillingBanner
                  banner={billingBanner}
                  onDismiss={() => setDismissedBillingBannerId(billingBanner.dismissKey)}
                />
              ) : null}
              {billingHardLocked ? (
                <div className="mx-auto max-w-2xl rounded-2xl border border-rose-200 bg-rose-50 p-6 text-rose-950 shadow-sm" data-testid="app-hard-lock">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-rose-700" />
                    <div>
                      <h1 className="text-lg font-semibold">{hardLockMessage}</h1>
                      <p className="mt-2 text-sm text-rose-900">
                        Only Billing and Referrals remain available until billing is restored.
                      </p>
                      <Link
                        to="/settings/billing"
                        className="mt-4 inline-flex rounded-lg bg-rose-900 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-800"
                      >
                        Open Billing
                      </Link>
                    </div>
                  </div>
                </div>
              ) : billingAccess?.mode === "read_only" ? (
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-700" />
                    <span>{readOnlyCopy.banner}</span>
                  </div>
                  <Link to="/settings/billing" className="font-semibold underline underline-offset-4">
                    Open Billing
                  </Link>
                </div>
              ) : null}
              {billingHardLocked ? null : <Outlet />}
            </TutorialProvider>
          </main>
        </div>
      </BranchProvider>
      <SupportChatBubble />
    </div>
  );
}

function GlobalBillingBanner({ banner, onDismiss }) {
  return (
    <div
      data-testid="global-billing-banner"
      className={[
        "mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm shadow-sm",
        banner.className,
      ].join(" ")}
    >
      <div className="flex min-w-0 items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span className="font-medium">{banner.text}</span>
      </div>
      <div className="ml-auto flex items-center gap-2">
        <Link
          to="/settings/billing"
          data-testid="global-billing-banner-action"
          className={banner.actionClassName}
        >
          {banner.actionLabel}
        </Link>
        <button
          type="button"
          data-testid="dismiss-global-billing-banner"
          onClick={onDismiss}
          className="rounded p-1 transition hover:bg-black/5"
          aria-label="Dismiss billing banner"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function readOnlyAccessCopy(reason) {
  if (reason === "tenant_suspended") {
    return {
      banner: "Account suspended: records remain visible, but new work is blocked until Dental OS reactivates the tenant.",
    };
  }

  return {
    banner: "Billing read-only mode: records remain visible, but new work is blocked until billing is restored.",
  };
}

function buildBillingUrgencyBanner(portal) {
  const invoice = portal?.open_invoice;
  const subscription = portal?.subscription || {};
  if (!invoice?.id || !["active", "active_pending_verification"].includes(subscription.status)) return null;
  if (Number(invoice.amount_centavos || 0) <= 0) return null;

  const pendingReview = invoice.status === "pending_verification"
    || portal?.latest_submission?.status === "pending_review"
    || portal?.invoice_submission?.status === "pending_review";

  if (pendingReview) {
    return {
      dismissKey: `${invoice.id}:review`,
      text: "Payment proof received. We're reviewing it now.",
      actionLabel: "View submission",
      className: "border-sky-200 bg-sky-50 text-sky-950",
      actionClassName: "rounded-lg bg-sky-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-800",
    };
  }

  const daysUntilDue = daysUntil(invoice.due_date);
  if (daysUntilDue == null || daysUntilDue > 3) return null;

  if (daysUntilDue < 0) {
    return {
      dismissKey: `${invoice.id}:overdue`,
      text: "Your payment is overdue. Settle now to avoid suspension.",
      actionLabel: "Pay Now",
      className: "border-rose-200 bg-rose-50 text-rose-950",
      actionClassName: "rounded-lg bg-rose-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-800",
    };
  }

  if (daysUntilDue === 0) {
    return {
      dismissKey: `${invoice.id}:today`,
      text: "Payment is due today.",
      actionLabel: "Pay Now",
      className: "border-orange-200 bg-orange-50 text-orange-950",
      actionClassName: "rounded-lg bg-orange-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-800",
    };
  }

  return {
    dismissKey: `${invoice.id}:soon`,
    text: `Payment due on ${formatDate(invoice.due_date)}. Pay now to avoid interruption.`,
    actionLabel: "Pay Now",
    className: "border-yellow-200 bg-yellow-50 text-yellow-950",
    actionClassName: "rounded-lg bg-yellow-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-yellow-800",
  };
}

function daysUntil(dueDate) {
  if (!dueDate) return null;
  const due = parseLocalDate(dueDate);
  if (!due) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / 86400000);
}

function parseLocalDate(value) {
  if (!value) return null;
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function formatDate(value) {
  const date = parseLocalDate(value);
  if (!date) return value;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
